import { yupResolver } from '@hookform/resolvers/yup';
import React, { useEffect, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import * as Yup from 'yup';

import { useAppSelector } from '@/app/hook';
import { PSButton } from '@/components/ui/button';
import { selectUseId } from '@/features/auth/auth.slice';
import { BlockType } from '@/interfaces/models/Knowledge/scenario.interface';

import AddNoteButton from '../../AddNoteButton';
import NodeParentChanger from '../../NodeParentChanger';
import NoteForm, { getNoteSchema, INoteForm } from '../../NoteForm';
import ScenarioHeader from '../../ScenarioHeader';
import { MacBotActionKind } from '../../types';
import TextScenarioDeleteButton from './scenario-delete-button';
import ScenarioSide from './scenario-side';
import { TextInputWithHint } from './TextInputWithHint';

export const DelayForm = ({
  setShow,
  itemData,
  parentId,
  tree,
  loading,
  createScenarioBlock,
  deleteScenarioBlock,
  changeScenarioBlock,
}: {
  setShow: (item: boolean) => void;
  itemData: any;
  loading: boolean;
  createScenarioBlock: (block: any) => Promise<void>;
  deleteScenarioBlock: (block: any) => Promise<void>;
  changeScenarioBlock: (block: any) => Promise<void>;
  parentId: string | null;
  tree: any[];
}) => {
  const { t } = useTranslation();
  const userId = useAppSelector(selectUseId);

  const components = {
    DropdownIndicator: null,
  };

  interface IEndConversationForm extends INoteForm {
    delay: boolean;
  }

  const schema = Yup.object({
    delay: Yup.number().typeError(t('MacDelayForm/key2')).required(t('MacDelayForm/key2')).min(0, t('MacDelayForm/key2')),
    ...getNoteSchema(t),
  });

  const [isDelete, setIsDelete] = useState(true);
  useEffect(() => {
    if (!itemData) {
      setIsDelete(false);
    }
  }, [itemData]);

  const {
    register,
    reset,
    setValue,
    handleSubmit,
    getValues,
    watch,
    formState: { errors },
    control,
  } = useForm<IEndConversationForm>({
    defaultValues: {
      delay: false,
      notes: [
        {
          note_id: uuidv4(),
          creation_date_time: new Date().toISOString(),
          title: '',
          description: '',
          qc: false,
          author: userId,
        },
      ],
    },
    resetOptions: {
      keepDefaultValues: true,
    },
    resolver: yupResolver(schema) as any,
    mode: 'onSubmit',
  });
  const { fields } = useFieldArray({
    control,
    name: 'notes',
  });

  useEffect(() => {
    reset();
  }, []);

  const deleteHandler = () => {
    deleteScenarioBlock(itemData).then(res => {
      setShow(false);
    });
  };

  useEffect(() => {
    if (itemData && itemData?.delay) {
      setValue('delay', itemData?.delay);
      setValue('notes', itemData?.notes);
    }
  }, [itemData]);

  const ButtonSubmit = (data: IEndConversationForm) => {
    if (itemData) {
      const _data = {
        ...itemData,
        id: itemData.id,
        type: BlockType.Delay,
        parent: itemData.parent,
        delay: data.delay,
        notes: data.notes,
      };
      changeScenarioBlock(_data).then(() => {
        setShow(false);
      });
    } else {
      const _data = {
        type: BlockType.Delay,
        parent: parentId,
        delay: data.delay,
        title: 'Delay',
        notes: data.notes,
      };
      createScenarioBlock(_data).then(() => {
        setShow(false);
      });
    }
  };

  return (
    <React.Fragment>
      <ScenarioSide onSubmit={handleSubmit(ButtonSubmit)} size="md">
        <ScenarioHeader
          type={MacBotActionKind.ShowDelayForm}
          onClose={() => {
            reset();
          }}
        />

        <div className="tw-self-stretch tw-h-[100px] tw-p-6 tw-bg-gray-50 tw-flex-col tw-justify-start tw-items-start tw-gap-4 tw-flex">
          <TextInputWithHint
            label={t('MacDelayForm/label')}
            name="title"
            register={register('delay')}
            error={errors.delay}
            type="number"
            min={0}
            ErrorMessage={t('MacDelayForm/unit')}
          />
        </div>
        <div className="tw-flex flex-row tw-justify-between tw-items-center">
          <div className="tw-flex tw-justify-start tw-items-center tw-gap-2">
            {isDelete && <TextScenarioDeleteButton itemData={itemData} tree={tree} deleteHandler={deleteHandler} />}

            {itemData?.id && <NodeParentChanger tree={tree} currentId={itemData.id} />}
            <AddNoteButton noteFormMethods={{ getValues, setValue }} />
          </div>
          <div className="tw-flex tw-justify-end">
            <PSButton id="btn-submit=delay" type="submit" disabled={loading} isLoading={loading} title={t('MacBotResponseForm/btn-02')} />
          </div>
        </div>

        <NoteForm noteFormMethods={{ register, watch, errors, fields, setValue }} />
      </ScenarioSide>
    </React.Fragment>
  );
};
